/**
 * PMHallowayAI — pure behaviour tree for PM Halloway's post-Praetor confrontation.
 *
 * Deliberate inversion of every other fight (MY-1978): PM Halloway stands
 * alone with no guards, exposed as a weak man. NOT a boss fight — a scripted
 * humiliation beat.
 *
 * Behaviour:
 *   - Backs away from the player at all times (flee).
 *   - Occasionally attempts a feeble_shove (0 dmg) when cornered, or a
 *     bribe_plea (0 dmg) as a theatrical alternate.
 *   - Player cannot lose: PM deals zero meaningful damage.
 *   - Any hit (light, heavy, throw, headbutt) KOs him (maxHp: 1 in entity def).
 *
 * No Phaser import — fully testable in Node.
 */
import { type EnemyAIInput } from '@/systems/EnemyAI';

// ── Range constants ────────────────────────────────────────────────────────────
/** Distance at which PM panics and attempts a feeble shove. */
export const SHOVE_RANGE = 80;
/** Windup frames for the feeble shove (slow, telegraphed). */
export const SHOVE_WINDUP_FRAMES = 22;
/** Windup frames for the bribe plea (longer — theatrical pause). */
export const BRIBE_WINDUP_FRAMES = 35;
/** Flee speed — PM Halloway is sedentary and not athletic. */
export const HALLOWAY_FLEE_SPEED = 0.9;
/** Base frames between feeble attempts (at aggressionScale = 0). */
const BASE_FEEBLE_COOLDOWN = 120;

// ── Types ──────────────────────────────────────────────────────────────────────
export type PMHallowayNode = 'scan' | 'flee' | 'feeble_windup' | 'bribe_windup';

export type PMHallowayAttack = 'feeble_shove' | 'bribe_plea';

export interface PMHallowayAIState {
  node: PMHallowayNode;
  cooldown: number;
  windup: number;
  /** Alternates 0 → 1 between feeble_shove and bribe_plea. */
  attemptCycle: number;
}

export type PMHallowayAIInput = EnemyAIInput;

export interface PMHallowayAIOutput {
  moveX: number;
  moveY: number;
  facing: 1 | -1;
  wantsAttack: boolean;
  attackType: PMHallowayAttack | null;
  nextState: PMHallowayAIState;
}

// ── Initial state ──────────────────────────────────────────────────────────────
export function initialPMHallowayState(): PMHallowayAIState {
  return {
    node: 'scan',
    cooldown: 0,
    windup: 0,
    attemptCycle: 0,
  };
}

// ── Main tick ──────────────────────────────────────────────────────────────────
/**
 * Advance PM Halloway's AI by one frame.
 * Pure function — the Phaser adapter applies movement and hitboxes.
 */
export function tickPMHallowayAI(
  input: PMHallowayAIInput,
  state: PMHallowayAIState,
): PMHallowayAIOutput {
  // Always face the player — the horror of eye contact.
  const facing: 1 | -1 = input.targetX >= input.selfX ? 1 : -1;

  if (input.isKO || input.isBeingThrown) {
    return noOp(facing, initialPMHallowayState());
  }

  const cooldown = Math.max(0, state.cooldown - 1);
  const windup = Math.max(0, state.windup - 1);

  // Hitstun: freeze, keep ticking cooldown.
  if (input.isHitstunned) {
    return noOp(facing, { ...state, node: 'scan', cooldown, windup: 0 });
  }

  // ── Windup: feeble_shove ──────────────────────────────────────────────────
  if (state.node === 'feeble_windup') {
    if (windup > 0) {
      return noOp(facing, { ...state, cooldown, windup });
    }
    return emitAttack(facing, 'feeble_shove', {
      node: 'flee',
      cooldown: feebleCooldown(input.aggressionScale),
      windup: 0,
      attemptCycle: nextCycle(state.attemptCycle),
    });
  }

  // ── Windup: bribe_plea ────────────────────────────────────────────────────
  if (state.node === 'bribe_windup') {
    if (windup > 0) {
      return noOp(facing, { ...state, cooldown, windup });
    }
    return emitAttack(facing, 'bribe_plea', {
      node: 'scan',
      cooldown: feebleCooldown(input.aggressionScale),
      windup: 0,
      attemptCycle: nextCycle(state.attemptCycle),
    });
  }

  const { dx, dy, dist } = toTarget(input);

  // ── Panic shove / bribe when cornered (player too close) ──────────────────
  if (cooldown === 0 && input.canAttack && dist <= SHOVE_RANGE) {
    const nextNode: PMHallowayNode = state.attemptCycle === 0 ? 'feeble_windup' : 'bribe_windup';
    const nextWindup = state.attemptCycle === 0 ? SHOVE_WINDUP_FRAMES : BRIBE_WINDUP_FRAMES;
    return noOp(facing, {
      ...state,
      cooldown,
      node: nextNode,
      windup: nextWindup,
    });
  }

  // ── Flee (primary behaviour — always move away from player) ──────────────
  if (dist > 8) {
    const len = dist || 1;
    return {
      moveX: (-dx / len) * HALLOWAY_FLEE_SPEED,
      moveY: (-dy / len) * HALLOWAY_FLEE_SPEED,
      facing,
      wantsAttack: false,
      attackType: null,
      nextState: { ...state, cooldown, windup: 0, node: 'flee' },
    };
  }

  return noOp(facing, { ...state, cooldown, node: 'scan' });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function feebleCooldown(aggressionScale: number): number {
  return Math.round(BASE_FEEBLE_COOLDOWN * (1 - aggressionScale * 0.3));
}

function toTarget(input: PMHallowayAIInput): { dx: number; dy: number; dist: number } {
  const dx = input.targetX - input.selfX;
  const dy = input.targetY - input.selfY;
  return { dx, dy, dist: Math.hypot(dx, dy) };
}

function noOp(facing: 1 | -1, nextState: PMHallowayAIState): PMHallowayAIOutput {
  return { moveX: 0, moveY: 0, facing, wantsAttack: false, attackType: null, nextState };
}

function emitAttack(
  facing: 1 | -1,
  attackType: PMHallowayAttack,
  nextState: PMHallowayAIState,
): PMHallowayAIOutput {
  return { moveX: 0, moveY: 0, facing, wantsAttack: true, attackType, nextState };
}

function nextCycle(c: number): number {
  return (c + 1) % 2;
}
